import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CompanyShell } from "@/components/CompanyShell";
import { AddPosition } from "@/components/PositionsPanel";
import { FileText, UploadCloud, Loader2, Check, Mail, Send, TriangleAlert } from "lucide-react";

export default function UploadCandidate() {
  const nav = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const [created, setCreated] = useState(null); // { id, name }
  const [ackEmail, setAckEmail] = useState("");
  const [ackMode, setAckMode] = useState("default");
  const [ackText, setAckText] = useState("");
  const [sendingAck, setSendingAck] = useState(false);
  const [dup, setDup] = useState(null);

  const companyName = user?.company_name || "naša spoločnosť";
  const defaultText =
    `Dobrý deň,\n\nďakujeme za Váš záujem o pracovnú pozíciu v spoločnosti ${companyName}. ` +
    `Vašu žiadosť/profil CV sme prijali a budeme sa mu v najbližších dňoch venovať. ` +
    `V prípade, že budete spĺňať naše požiadavky, budeme Vás kontaktovať telefonicky alebo e-mailom.\n\n` +
    `Ďakujeme za Váš čas a prajeme Vám veľa úspechov.\n\n${companyName}`;

  const upload = async (force = false) => {
    if (!file) { inputRef.current?.click(); return; }
    setDup(null); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/company/upload-cv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { ...(force ? { force: true } : {}) },
      });
      toast.success(`${data.name} pridaný do databázy`);
      setAckEmail(data.email || "");
      setCreated({ id: data.id, name: data.name, title: data.title || "", cv_area: data.cv_area || "", ai_area: data.recommended_area || "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d && d.duplicate) {
        setDup(d.existing);
      } else {
        toast.error(formatApiError(d) || err.message);
      }
    } finally { setBusy(false); }
  };

  const sendAck = async () => {
    if (!ackEmail.trim()) return;
    if (ackMode === "custom" && !ackText.trim()) { toast.error("Napíšte text správy"); return; }
    setSendingAck(true);
    try {
      const r = await api.post(`/company/candidate/${created.id}/ack-email`, {
        email: ackEmail.trim(), mode: ackMode, text: ackMode === "custom" ? ackText : undefined,
      });
      toast.success(r.data.mock ? "Email pripravený (mock režim)" : "Email odoslaný kandidátovi");
      nav(`/company/candidate/${created.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSendingAck(false); }
  };

  const skip = () => nav(`/company/candidate/${created.id}`);
  const cancelAck = () => nav("/company/database");

  return (
    <CompanyShell>
      <h1 className="font-head text-3xl font-bold tracking-tight">Nahrať CV kandidáta</h1>
      <p className="mt-2 text-slate-600">AI z CV automaticky vytvorí profil a uloží ho do vašej databázy.</p>

      <div className="mt-8 max-w-xl">
        <div data-testid="company-cv-dropzone" onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]); }}
          className="rounded-3xl border-2 border-dashed border-slate-300 bg-white p-12 flex flex-col items-center text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/40 transition-colors">
          <div className="h-16 w-16 rounded-2xl bg-blue-50 flex items-center justify-center">
            {file ? <FileText className="text-blue-700" size={30} /> : <UploadCloud className="text-blue-700" size={30} />}
          </div>
          <p className="mt-4 font-semibold text-slate-800 break-all">{file ? file.name : "Ťuknite a nahrajte CV"}</p>
          <p className="text-sm text-slate-500 mt-1">PDF, DOCX alebo TXT</p>
        </div>
        <input ref={inputRef} type="file" accept=".pdf,.docx,.doc,.txt" hidden data-testid="company-cv-input"
          onChange={(e) => setFile(e.target.files?.[0])} />

        <button data-testid="company-analyze-btn" onClick={() => upload(false)} disabled={busy}
          className="mt-6 inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-8 py-4 font-semibold transition-colors active:scale-95 disabled:opacity-60">
          {busy ? <><Loader2 className="animate-spin" size={18} /> AI vytvára profil…</> : <><Check size={18} /> Vytvoriť profil</>}
        </button>
      </div>

      {created && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-3xl p-6 max-h-[90vh] overflow-y-auto" data-testid="ack-email-modal">
            <h2 className="font-head text-xl font-bold flex items-center gap-2"><Mail size={20} className="text-blue-700" /> Odoslať email kandidátovi</h2>
            <p className="mt-2 text-sm text-slate-600">Profil <strong>{created.name}</strong> bol vytvorený. Vyberte text a potvrďte odoslanie potvrdzujúceho emailu.</p>

            <AreaAssign created={created} />

            <label className="block mt-4">
              <span className="text-sm font-semibold text-slate-700">Email kandidáta</span>
              <input data-testid="ack-email-input" type="email" value={ackEmail} onChange={(e) => setAckEmail(e.target.value)}
                placeholder="kandidat@email.sk"
                className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
            </label>

            <div className="mt-4 space-y-2">
              <OptionCard testId="ack-mode-default" active={ackMode === "default"} onClick={() => setAckMode("default")}
                title="Predvolený text" desc="Automatické poďakovanie za záujem s názvom vašej firmy" />
              <OptionCard testId="ack-mode-custom" active={ackMode === "custom"} onClick={() => setAckMode("custom")}
                title="Vlastný text" desc="Napíšte kandidátovi vlastnú správu" />
            </div>

            {ackMode === "default" ? (
              <div data-testid="ack-default-preview" className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600 whitespace-pre-line">
                {defaultText}
              </div>
            ) : (
              <textarea data-testid="ack-custom-text" value={ackText} onChange={(e) => setAckText(e.target.value)} rows={7}
                placeholder="Napíšte vlastný text emailu…"
                className="mt-3 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
            )}

            <p className="mt-2 text-[11px] text-amber-600">Pozn.: v testovacom (sandbox) režime Resend doručí email iba na overenú adresu vlastníka účtu, kým sa neoverí doména.</p>

            <div className="mt-4 flex gap-3">
              <button data-testid="ack-skip-btn" onClick={skip}
                className="flex-1 rounded-full border border-slate-300 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Preskočiť</button>
              <button data-testid="ack-cancel-btn" onClick={cancelAck}
                className="flex-1 rounded-full border border-slate-300 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Zrušiť</button>
              <button data-testid="ack-send-btn" onClick={sendAck} disabled={sendingAck || !ackEmail.trim()}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-700 text-white rounded-full py-3 text-sm font-semibold hover:bg-blue-800 transition-colors disabled:opacity-60">
                {sendingAck ? <Loader2 className="animate-spin" size={17} /> : <Send size={17} />} Odoslať
              </button>
            </div>
          </div>
        </div>
      )}
      {dup && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl p-6" data-testid="duplicate-modal">
            <h2 className="font-head text-xl font-bold flex items-center gap-2"><TriangleAlert size={20} className="text-amber-500" /> Podobný kandidát už existuje</h2>
            <p className="mt-2 text-sm text-slate-600">V databáze sa našiel profil s rovnakým menom alebo emailom:</p>
            <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="font-head font-semibold text-slate-900">{dup.name}</div>
              {dup.title && <div className="text-sm text-slate-500">{dup.title}</div>}
              {dup.email && <div className="text-sm text-slate-500 mt-0.5">{dup.email}</div>}
              <div className="text-xs text-slate-400 mt-1">
                Pridaný {dup.created_at ? new Date(dup.created_at).toLocaleDateString("sk-SK") : "—"}{dup.archived ? " · archivovaný" : ""}
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-600">Chcete napriek tomu nahrať CV znova, alebo zrušiť?</p>
            <div className="mt-4 flex gap-3">
              <button data-testid="dup-cancel-btn" onClick={() => setDup(null)}
                className="flex-1 rounded-full border border-slate-300 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Zrušiť</button>
              <button data-testid="dup-force-btn" onClick={() => upload(true)} disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-700 text-white rounded-full py-3 text-sm font-semibold hover:bg-blue-800 transition-colors disabled:opacity-60">
                {busy ? <Loader2 className="animate-spin" size={17} /> : <UploadCloud size={17} />} Nahrať znova
              </button>
            </div>
          </div>
        </div>
      )}
    </CompanyShell>
  );
}

function OptionCard({ testId, active, onClick, title, desc }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`w-full text-left rounded-xl border p-3 transition-colors ${active ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
      <div className="flex items-center gap-2">
        <span className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${active ? "border-blue-600 bg-blue-600" : "border-slate-300"}`} />
        <span className={`font-semibold text-sm ${active ? "text-blue-700" : "text-slate-800"}`}>{title}</span>
      </div>
      <p className="text-xs text-slate-500 mt-1 ml-6">{desc}</p>
    </button>
  );
}

const MODES = [
  { v: "cv", l: "Automaticky podľa CV" },
  { v: "ai", l: "Priradené podľa AI" },
  { v: "manual", l: "Manuálne zo zoznamu" },
];

function AreaAssign({ created }) {
  const [mode, setMode] = useState("cv");
  const [done, setDone] = useState([]);
  const reload = async () => {
    const r = await api.get(`/company/candidate/${created.id}`);
    setDone(r.data.positions || []);
  };
  const init = mode === "cv" ? { name: created.title, area: created.cv_area }
    : mode === "ai" ? { name: created.title, area: created.ai_area } : { name: "", area: "" };
  return (
    <div className="mt-4 rounded-2xl bg-violet-50/50 border border-violet-100 p-4" data-testid="area-assign-block">
      <div className="text-sm font-semibold text-[#1e2a5a]">Zaradenie do oblasti a pozície</div>
      <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid="area-mode">
        {MODES.map((m) => (
          <button key={m.v} data-testid={`area-mode-${m.v}`} onClick={() => setMode(m.v)}
            className={`rounded-xl border px-2 py-2 text-[11px] font-semibold leading-tight transition-colors ${mode === m.v ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>{m.l}</button>
        ))}
      </div>
      {mode !== "manual" && (
        <p className="mt-2 text-xs text-slate-600" data-testid="area-suggestion">
          {init.area ? <>Návrh: <strong>{init.name || "pozícia z CV"}</strong> → <strong className="text-violet-700">{init.area}</strong>. Môžete upraviť pred zaradením.</> : "Návrh nie je k dispozícii – vyberte oblasť ručne."}
        </p>
      )}
      <AddPosition profileId={created.id} onAdded={reload} assignedBy={mode} initialName={init.name} initialArea={init.area} />
      {done.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="assigned-positions">
          {done.map((x) => <span key={x.id} className="rounded-full bg-violet-600 px-2.5 py-1 text-[11px] font-semibold text-white">{x.name} · {x.area}</span>)}
        </div>
      )}
    </div>
  );
}
